import React, { useState, useEffect, useRef } from 'react';
import {
  collection, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from './firebase';
import './Waitlist.css';

/* ── Scroll reveal hook ──────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { el.classList.add('wl-visible'); observer.disconnect(); }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ── Email submission ────────────────────────────────────── */
async function submitEmail(email) {
  const normalized = email.trim().toLowerCase();
  await addDoc(collection(db, 'waitlist'), {
    email: normalized,
    timestamp: serverTimestamp(),
    source: 'waitlist_page',
  });
  return { status: 'success' };
}

/* ── WaitlistForm ────────────────────────────────────────── */
function WaitlistForm({ variant = 'light' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const result = await submitEmail(email);
      setStatus(result.status);
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <div className={`wl-form wl-form--${variant}`}>
        <div className="wl-form__success">
          <div className="wl-form__success-icon">✓</div>
          <div className="wl-form__success-text">
            <strong>Du bist dabei.</strong>
            <span>Wir melden uns, sobald der Zugang startet.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className={`wl-form wl-form--${variant}`} onSubmit={handleSubmit}>
      <div className="wl-form__row">
        <input
          className="wl-form__input"
          type="email"
          placeholder="deine@email.de"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
          disabled={status === 'loading'}
          autoComplete="email"
        />
        <button
          className="wl-btn wl-btn--amber wl-btn--lg"
          type="submit"
          disabled={status === 'loading' || !email.trim()}
        >
          {status === 'loading'
            ? <span className="wl-form__spinner" />
            : 'Zugang sichern'}
        </button>
      </div>
      {status === 'error' && (
        <p className="wl-form__error">
          Etwas ist schiefgelaufen. Bitte versuche es erneut.
        </p>
      )}
    </form>
  );
}

/* ── NavBar ──────────────────────────────────────────────── */
function WlNavBar() {
  useEffect(() => {
    const nav = document.querySelector('.wl-nav');
    const onScroll = () => nav.classList.toggle('wl-nav--scrolled', window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <nav className="wl-nav">
      <div className="wl-nav__inner">
        <span className="wl-nav__logo">
          <img src="/logo.png" alt="CVFlow" className="wl-nav__logo-dot" />
          CVFlow
        </span>
      </div>
    </nav>
  );
}

/* ── Hero ────────────────────────────────────────────────── */
function WlHero() {
  const ref = useReveal();
  const trust = ['Kostenlos in der Beta', 'Kein Spam', 'Dauert unter 60 Sekunden'];
  return (
    <section className="wl-hero">
      <div className="wl-hero__grid" />
      <div className="wl-hero__glow" />
      <div className="wl-hero__inner wl-reveal" ref={ref}>
        <div className="wl-hero__eyebrow">Früher Zugang · Begrenzte Plätze</div>
        <h1 className="wl-hero__title">
          Bewerben ohne<br />
          <em>hängen zu bleiben.</em>
        </h1>
        <p className="wl-hero__sub">
          Von der Stellenanzeige zur fertigen Bewerbung – in einem klaren Ablauf,
          der dir die Arbeit abnimmt.
        </p>
        <WaitlistForm variant="light" />
        <div className="wl-hero__trust">
          {trust.map(t => (
            <div className="wl-hero__trust-item" key={t}>
              <span className="wl-hero__trust-dot" />
              {t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Problem ─────────────────────────────────────────────── */
function WlProblem() {
  const ref = useReveal();
  return (
    <section className="wl-section wl-section--teal">
      <div className="wl-section__inner wl-reveal" ref={ref}>
        <span className="wl-section__label">Das Problem</span>
        <h2 className="wl-problem__title">
          Der Bewerbungsprozess kostet mehr<br />Energie als er sollte.
        </h2>
        <div className="wl-problem__body">
          <p>
            Du findest eine Stelle, passt deinen Lebenslauf an, formulierst dein Anschreiben –
            und jedes Mal fängst du wieder von vorne an.
          </p>
          <p>Das Problem ist nicht, dass du es nicht kannst.</p>
          <p>Das Problem ist, dass jeder Schritt Zeit kostet und dich ausbremst.</p>
        </div>
        <div className="wl-problem__chips">
          <span className="wl-pain-chip">Jedes Mal neu formulieren</span>
          <span className="wl-pain-chip">Unsicher ob man überhaupt passt</span>
          <span className="wl-pain-chip">Stunden für ein Dokument</span>
        </div>
      </div>
    </section>
  );
}

/* ── Solution ────────────────────────────────────────────── */
function WlSolution() {
  const ref = useReveal();
  return (
    <section className="wl-section wl-section--beige">
      <div className="wl-section__inner wl-reveal" ref={ref}>
        <span className="wl-section__label">Die Lösung</span>
        <h2 className="wl-solution__title">
          Ein klarer Ablauf statt<br />jedes Mal neu anfangen.
        </h2>
        <div className="wl-solution__body">
          <p>Dieses Tool führt dich Schritt für Schritt von der Stelle zur fertigen Bewerbung.</p>
          <p>Ohne Umwege. Ohne Leerlauf.</p>
          <p>Du weißt immer, was als Nächstes zu tun ist.</p>
        </div>
      </div>
    </section>
  );
}

/* ── Workflow ─────────────────────────────────────────────── */
function WlWorkflow() {
  const ref = useReveal();
  const c0 = useReveal(), c1 = useReveal(), c2 = useReveal();
  const cards = [c0, c1, c2];
  const steps = [
    { n: '01', title: 'Stelle einfügen', desc: 'Füge den Jobtext oder Link ein. Fertig.' },
    { n: '02', title: 'Einschätzung bekommen', desc: 'Sieh sofort, ob sich die Bewerbung lohnt und worauf es ankommt.' },
    { n: '03', title: 'Bewerbung fertigstellen', desc: 'Deine Unterlagen werden passend vorbereitet – bereit zum Abschicken.' },
  ];
  return (
    <section className="wl-section wl-section--beige-dark">
      <div className="wl-section__inner">
        <h2 className="wl-workflow__title wl-reveal" ref={ref}>So funktioniert's.</h2>
        <div className="wl-workflow__grid">
          {steps.map((s, i) => (
            <div
              className="wl-step-card wl-reveal"
              key={s.n}
              ref={cards[i]}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="wl-step-card__num">{s.n}</div>
              <div className="wl-step-card__title">{s.title}</div>
              <div className="wl-step-card__desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── ValueProp ───────────────────────────────────────────── */
function WlValueProp() {
  const ref = useReveal();
  const bullets = [
    'Kein ständiges Umformulieren',
    'Kein Rätselraten, ob du passt',
    'Kein Aufschieben mehr',
  ];
  return (
    <section className="wl-section wl-section--teal">
      <div className="wl-section__inner wl-reveal" ref={ref}>
        <span className="wl-section__label">Was du gewinnst</span>
        <h2 className="wl-valueprop__title">Weniger Aufwand.<br />Mehr Bewerbungen.</h2>
        <ul className="wl-valueprop__bullets">
          {bullets.map(b => (
            <li className="wl-valueprop__bullet" key={b}>
              <span className="wl-valueprop__bullet-dot" />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ── Differentiator ──────────────────────────────────────── */
function WlDifferentiator() {
  const ref = useReveal();
  return (
    <section className="wl-section wl-section--beige">
      <div className="wl-section__inner wl-reveal" ref={ref}>
        <div className="wl-diff__accent" />
        <h2 className="wl-diff__title">
          Bevor du Zeit investierst,<br />weißt du ob es Sinn macht.
        </h2>
        <div className="wl-diff__body">
          <p>Du bekommst zuerst eine Einschätzung zur Stelle.</p>
          <p>
            So verschwendest du keine Zeit auf Bewerbungen, die kaum Chancen haben –
            und kannst dich auf die konzentrieren, die wirklich zählen.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── CTA Block ───────────────────────────────────────────── */
function WlCtaBlock() {
  const ref = useReveal();
  return (
    <div className="wl-ctablock">
      <div className="wl-ctablock__inner wl-reveal" ref={ref}>
        <h2 className="wl-ctablock__title">Teste den Ablauf vor allen anderen.</h2>
        <p className="wl-ctablock__sub">Kostenlos. Kein Spam.</p>
        <WaitlistForm variant="dark" />
      </div>
    </div>
  );
}

/* ── Momentum ────────────────────────────────────────────── */
function WlMomentum() {
  const ref = useReveal();
  return (
    <section className="wl-section wl-section--beige-dark">
      <div className="wl-section__inner wl-reveal" ref={ref}>
        <span className="wl-section__label">Warum das wichtig ist</span>
        <h2 className="wl-momentum__title">
          Bleib im Flow statt immer<br />wieder neu anzufangen.
        </h2>
        <div className="wl-momentum__body">
          <p>Die größte Hürde ist nicht das Schreiben.</p>
          <p>Es ist das Dranbleiben.</p>
          <p>
            Ein klarer Ablauf hilft dir, schneller ins Handeln zu kommen –
            und dabei zu bleiben.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ───────────────────────────────────────────── */
function WlFinalCta() {
  const ref = useReveal();
  return (
    <section className="wl-finalcta">
      <div className="wl-finalcta__grid" />
      <div className="wl-finalcta__glow" />
      <div className="wl-finalcta__inner wl-reveal" ref={ref}>
        <span className="wl-finalcta__eyebrow">Frühzugang zur Beta</span>
        <h2 className="wl-finalcta__title">
          Mach den Bewerbungsprozess<br />einfacher.
        </h2>
        <p className="wl-finalcta__sub">Jetzt Zugang sichern – kostenlos, kein Spam.</p>
        <WaitlistForm variant="dark" />
      </div>
    </section>
  );
}

/* ── Footer ──────────────────────────────────────────────── */
function WlFooter() {
  return (
    <footer className="wl-footer">
      <div className="wl-footer__inner">
        <span className="wl-footer__brand">
          <img src="/logo.png" alt="CVFlow" className="wl-footer__brand-dot" />
          CVFlow
        </span>
        <p className="wl-footer__note">
          Früher Zugang für Tester. Feedback hilft, das Produkt weiterzuentwickeln.
        </p>
        <nav className="wl-footer__links">
          <Link to="/datenschutz">Datenschutz</Link>
          <Link to="/impressum">Impressum</Link>
        </nav>
      </div>
    </footer>
  );
}

/* ── Page ────────────────────────────────────────────────── */
export default function Waitlist() {
  return (
    <div className="wl-root">
      <WlNavBar />
      <WlHero />
      <WlProblem />
      <WlSolution />
      <WlWorkflow />
      <WlValueProp />
      <WlDifferentiator />
      <WlCtaBlock />
      <WlMomentum />
      <WlFinalCta />
      <WlFooter />
    </div>
  );
}
